import React from 'react';
import { inDOM } from '../../utils';
import { IntersectionContext, IntersectionContextValues } from './IntersectionContext';

type ElementWithCallback = {
    element: HTMLElement;
    onObserve: () => void;
};

export type IntersectionProviderProps = {
    /**
     * The child components.
     */
    children: React.ReactNode;

    /**
     * Options applied to the IntersectionObserver.
     */
    observerOptions?: IntersectionObserverInit;
};

export function IntersectionProvider(props: IntersectionProviderProps): React.JSX.Element {
    const { children, observerOptions: options } = props;

    const isClient = inDOM();

    const observerRef = React.useRef<IntersectionObserver | null>(null);
    const elementsWithCallbacks = React.useRef<ElementWithCallback[]>([]);

    const handleEntry = React.useCallback((entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const target = entry.target as HTMLElement;

                // Find the callback associated with this element
                const elementWithCallback = elementsWithCallbacks.current.find((item) => item.element === target);

                if (elementWithCallback) {
                    elementWithCallback.onObserve(); // Fire the callback
                    observerRef.current?.unobserve(target); // Stop observing the element
                }
            }
        });
    }, []);

    const handleRef = React.useCallback(
        (onObserve: () => void) => (element: HTMLElement | null) => {
            if (isClient && element) {
                if (!observerRef.current) {
                    observerRef.current = new IntersectionObserver(handleEntry, options);
                }
                elementsWithCallbacks.current.push({ element, onObserve });
                observerRef.current.observe(element);
            }
        },
        [handleEntry, isClient, options]
    );

    React.useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const values: IntersectionContextValues = React.useMemo(
        () => ({
            handleRef,
        }),
        [handleRef]
    );

    return <IntersectionContext.Provider value={values}>{children}</IntersectionContext.Provider>;
}
